import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Container,
    Grid,
    Skeleton,
    Stack,
    Chip,
    Modal,
    useMediaQuery,
} from '@mui/material';
import '../styles/productDetailCard.css';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useSelector } from 'react-redux';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 999,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    width: {
        xs: '90%',
        sm: '80%',
        xl: '100%',
    },
    maxWidth: 'lg',
    borderRadius: { xs: 2 },
};

const ProductImageComponent = ({ logoUrl, altText }) => (
    <Box
        sx={{
            width: '100%',
            minWidth: '280px',
            maxWidth: '300px',
            height: { xs: '180px', sm: '200px', lg: '200px' },
            overflow: { xs: 'hidden' },
            display: { xs: 'flex' },
            alignItems: { xs: 'center' },
            justifyContent: { xs: 'center' },
            zIndex: 1
        }}
    >
        <Box
            component="img"
            src={logoUrl}
            alt={altText}
            sx={{
                width: '100%',
                height: { xs: '180px', sm: '200px', lg: '200px' },
                objectFit: { xs: 'contain', sm: 'contain' },
                borderRadius: { xs: 1, sm: 2 },
                zIndex: 9,
            }}
        />
    </Box>
);

const StatusChip = ({ label, active }) => (
    <Chip
        icon={
            active ? (
                <CheckCircleIcon sx={{ color: "#2ab638 !important" }} />
            ) : (
                <CancelIcon sx={{ color: "#ea0c0c !important" }} />
            )
        }
        label={label}
        sx={{
            color: active ? "#0fab22" : "#ff2121",
            backgroundColor: active ? "#caf6be" : "#f6bebe",
            fontSize: "0.9rem",
            fontWeight: 500,
        }}
    />
);

export const RedeemTypeChip = ({ type }) => {
    const v = type?.toUpperCase();

    const showOnline = v === "ONLINE" || v === "BOTH";
    const showStore = v === "IN_STORE" || v === "OFFLINE" || v === "BOTH";

    return (
        <Stack direction="row" spacing={1}>
            <StatusChip label="Online" active={showOnline} />
            <StatusChip label="Store" active={showStore} />
        </Stack>
    );
};

const ProductDetailCard = ({ pageLoading }) => {
    const PRODUCTDETAILS = useSelector((state) => state?.ProductDetail?.allData);
    const [open, setOpen] = React.useState(false);
    const [characterNo, setCharacterNo] = useState(540);
    const lgDevices = useMediaQuery('(min-width:900px)');

    useEffect(() => {
        if (!lgDevices) {
            setCharacterNo(260);
        } else {
            setCharacterNo(540);
        }
    }, [lgDevices]);

    function handleShowMore(description) {
        if (description?.length > characterNo) {
            return description.slice(0, characterNo - 3) + '......';
        }
        return description;
    }

    const productDiscount = (discountKey, discount) => {
        return (
            <Chip
                label={`${discountKey || 'Discount'} : ${discount}%`}
                sx={{
                    fontSize: { xs: '.7rem', sm: '1rem' },
                    fontWeight: 500,
                    color: 'white.main',
                    backgroundColor: 'primary.main',
                }}
            />
        );
    };


    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    return (
        <Container
            maxWidth="xl"
            className="product_detail_mcontainer"
            sx={{
                px: { xs: 1, sm: 0 },
                backgroundColor: 'transparent',
            }}
        >
            <Grid
                container
                sx={{
                    justifyContent: { xs: 'flex-start', sm: 'center' },
                    minHeight: { xs: '230px', sm: '240px', lg: 170 },
                    pt: { xs: 0, sm: 3, md: 2, lg: 2 },
                    pb: { xs: 0, sm: '1.5rem', lg: 0 },
                    px: { xs: 0, sm: '1.5rem' },
                }}
            >
                <Grid
                    item
                    xs={12}
                    md={5}
                    lg={4}
                    sx={{
                        px: { xs: 1 },
                        mb: { xs: 3, lg: 0 },
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    {pageLoading || PRODUCTDETAILS?.images?.length === 0 || !PRODUCTDETAILS?.logoUrl ? (
                        <Skeleton variant="rounded" animation="wave" width="100%" height="170px" sx={{ maxWidth: "280px" }} />
                    ) : (
                        <ProductImageComponent
                            logoUrl={PRODUCTDETAILS?.logoUrl || PRODUCTDETAILS?.images?.[0]}
                            altText={PRODUCTDETAILS?.altText || PRODUCTDETAILS?.name}
                        />
                    )}
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={7}
                    lg={7}
                    sx={{
                        pt: { xs: '5px', sm: 1, lg: 1 },
                        pb: { xs: '5px', sm: '20px', lg: 1 },
                        px: { xs: '10px', sm: '20px' },
                    }}
                >
                    <Box>
                        {!pageLoading ? (
                            <Typography
                                variant="h1"
                                gutterBottom
                                sx={{
                                    fontSize: { sm: '1.5rem', lg: '1.5rem' },
                                    color: 'primary.main',
                                    fontWeight: 500,
                                    textAlign: { sm: 'center', md: 'left' },
                                }}
                            >
                                {PRODUCTDETAILS?.name}
                            </Typography>
                        ) : (
                            <Skeleton
                                variant="text"
                                animation="wave"
                                sx={{ fontSize: '2rem', color: 'primary.main', fontWeight: 400 }}
                            />
                        )}

                        {!pageLoading ? (
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    mt: { xs: 2, sm: 2, lg: 1 },
                                    py: { lg: '4px' },
                                    justifyContent: { sm: 'center', md: 'flex-start' },
                                }}
                            >
                                {PRODUCTDETAILS &&
                                    productDiscount(PRODUCTDETAILS.productType, PRODUCTDETAILS.discount)}
                                <RedeemTypeChip type={PRODUCTDETAILS?.usageLocation} />
                            </Stack>
                        ) : (
                            <Skeleton variant="text" animation="wave" sx={{ width: '100%', height: '60px' }} />
                        )}

                        {!pageLoading ? (
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: 'textPrimary.main',
                                    fontSize: { sm: '1.1rem', lg: '1.2rem' },
                                    fontWeight: 400,
                                    mt: { xs: 2, sm: 2, lg: 1 },
                                    mb: { xs: 1, lg: '1rem' },
                                }}
                            >
                                {handleShowMore(PRODUCTDETAILS?.description)}
                            </Typography>
                        ) : (
                            <Skeleton variant="text" animation="wave" sx={{ width: '100%', height: '30px' }} />
                        )}

                        {PRODUCTDETAILS?.description?.length > characterNo && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: {
                                        xs: 'flex-start',
                                        sm: 'flex-end',
                                        md: 'flex-start',
                                    },
                                    alignItems: 'center',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={handleOpen}
                                    sx={{
                                        mt: { xs: 0, sm: 1, lg: 0 },
                                        mb: { xs: 0, lg: 1 },
                                    }}
                                >
                                    Details
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>

            {/* Details Modal */}
            <Modal open={open} onClose={handleClose}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" component="h2">
                        {PRODUCTDETAILS?.title}
                    </Typography>
                    <Typography sx={{ mt: 2 }}>{PRODUCTDETAILS?.description}</Typography>
                    <Button variant="contained" onClick={handleClose} sx={{ mt: 3 }}>
                        Close
                    </Button>
                </Box>
            </Modal>
        </Container>
    );
};

export default ProductDetailCard;
